/**
 * ProtoServiceTemplate
 *
 * Landscape A4 service report template using jsPDF.
 * Layout:
 *   Title (top-left) ▸ STM logo (top-right) ▸
 *   Header table (Kunde/Customer | Location | Report Number | Object | Serial No./ID) ▸
 *   Summary (flex-fill) ▸ Service Status ▸ Signatures ▸ Footer
 */

import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { CONTROL_CARD_CONFIG, CONTROL_CARD_TEXT } from '@/features/control-card/config'
import { ImageRenderer } from '@/features/control-card/infrastructure'
import { STM_LOGO_SVG, getSignature } from './ndtPdfShared'
import { drawMethodTitle } from './pdfTitle'

// Extend jsPDF type for lastAutoTable
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable: { finalY: number; table: any }
  }
}

// ─── Layout constants (landscape A4: 297mm × 210mm) ──────────────────────────
const PAGE_W = 297
const PAGE_H = 210
const M_TOP = 10
const M_LR = 12
const M_BOTTOM = 12
const CONTENT_W = PAGE_W - 2 * M_LR
const TABLE_X = M_LR

const BORDER_COLOR: [number, number, number] = [170, 170, 170]
const TEXT_COLOR: [number, number, number] = [0, 0, 0]
const LABEL_COLOR: [number, number, number] = [68, 68, 68]
const BORDER_W = 0.1
const LABEL_SIZE = 6.5
const VALUE_SIZE = 8

// ─── Data interfaces ─────────────────────────────────────────────────────────

export interface ServiceEntryItem {
  value: string
  details: string
  comment: string
}

export interface ServiceReportData {
  reportNumber: string
  customer: string
  location?: string
  dateDuration?: string
  object: string
  serialNumber: string
  workPerformedItems: ServiceEntryItem[]
  sparePartsItems: ServiceEntryItem[]
  findings?: string
  workCompletion?: string
  recommendedActions?: string
  inlineImages?: string[]
  status: string
  issuedBy: string
  issuedDate: string
  verifiedBy?: string
  issuedBySignature?: string
  verifiedBySignature?: string
  attachedImages?: Array<{
    id: string
    image: string
    comment?: string
  }>
}

export interface ServiceTemplateOptions {
  finalize?: boolean
  logo?: string
}

/**
 * Landscape A4 service report template.
 */
export class ProtoServiceTemplate {
  private doc: jsPDF
  private readonly imageRenderer: ImageRenderer

  constructor() {
    this.doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true,
      floatPrecision: 'smart',
    })
    this.imageRenderer = new ImageRenderer(CONTROL_CARD_CONFIG)
  }

  async render(
    data: ServiceReportData,
    options?: ServiceTemplateOptions,
  ): Promise<jsPDF> {
    this.doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true,
      floatPrecision: 'smart',
    })

    let y = await this.drawHeader(data, options?.logo)
    y = this.drawHeaderTable(data, y)
    y = this.drawServiceContent(data, y)
    y = await this.drawSignatures(data, y)

    if (data.attachedImages && data.attachedImages.length > 0) {
      await this.drawImagePages(data.attachedImages, options?.logo)
    }

    if (!options?.finalize) {
      this.addWatermark()
    }

    this.drawFooterOnAllPages()

    return this.doc
  }

  getBlob(): Blob {
    return this.doc.output('blob')
  }

  getDataUrl(): string {
    return this.doc.output('dataurlstring')
  }

  save(filename: string): void {
    this.doc.save(filename)
  }

  // ─── Section renderers ─────────────────────────────────────────────────────

  private async drawHeader(
    data: ServiceReportData,
    logo?: string,
  ): Promise<number> {
    const doc = this.doc
    const y = M_TOP
    const logoW = 40
    const logoH = 12

    drawMethodTitle(doc, 'Service', TABLE_X, y + 8, { trailing: 'Report' })

    if (logo) {
      await this.imageRenderer.addLogo(doc, logo, {
        x: PAGE_W - M_LR - logoW,
        y: y + 2,
        width: logoW,
        height: logoH,
      })
    } else {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.setTextColor(...TEXT_COLOR)
      doc.text('STM INSPECTION AS', PAGE_W - M_LR, y + 8, { align: 'right' })
    }

    return y + 18
  }

  /**
   * Header table: Report No | Customer | Location | Date/Duration | Object | Serial No./ID
   */
  private drawHeaderTable(data: ServiceReportData, startY: number): number {
    autoTable(this.doc, {
      head: [[
        'Report No.',
        'Kunde/Customer',
        'Location',
        'Date / Duration',
        'Object',
        'Serial No./ID',
      ]],
      body: [[
        data.reportNumber,
        data.customer,
        data.location || '',
        data.dateDuration || '',
        data.object,
        data.serialNumber,
      ]],
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: {
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: { top: 0.5, right: 2, bottom: 0, left: 2 },
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        valign: 'middle',
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W * 0.12 },
        1: { cellWidth: CONTENT_W * 0.22 },
        2: { cellWidth: CONTENT_W * 0.16 },
        3: { cellWidth: CONTENT_W * 0.14 },
        4: { cellWidth: CONTENT_W * 0.22 },
        5: { cellWidth: CONTENT_W * 0.14 },
      },
    })
    return this.doc.lastAutoTable.finalY
  }

  /**
   * Service content section:
   *   Work performed rows   (one per entry): Work performed | Details | Comment
   *   Spare Parts rows      (one per entry): Spare Parts used | Details | Comment
   *   Findings              (full-width)
   *   Work completion       (full-width)
   *   Recommended action(s) (full-width, flex-fills remaining space)
   */
  private drawServiceContent(data: ServiceReportData, startY: number): number {
    const doc = this.doc
    const headerPad = { top: 0.5, right: 2, bottom: 0, left: 2 }
    const bodyPad = { top: 2, right: 3, bottom: 2, left: 3 }

    const workItems = data.workPerformedItems.length > 0
      ? data.workPerformedItems
      : [{ value: '', details: '', comment: '' }]

    const col1W = CONTENT_W * 0.34
    const commentW = CONTENT_W * 0.22

    const workHasComments = workItems.some((item) => item.comment.trim())
    const workHead = workHasComments
      ? [['Work performed', 'Details on work performed', 'Comment']]
      : [['Work performed', 'Details on work performed']]
    const workBody = workItems.map((item) =>
      workHasComments ? [item.value, item.details, item.comment] : [item.value, item.details],
    )
    const workColStyles: Record<number, { cellWidth: number }> = workHasComments
      ? { 0: { cellWidth: col1W }, 1: { cellWidth: CONTENT_W - col1W - commentW }, 2: { cellWidth: commentW } }
      : { 0: { cellWidth: col1W }, 1: { cellWidth: CONTENT_W - col1W } }

    autoTable(doc, {
      head: workHead,
      body: workBody,
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: { lineColor: BORDER_COLOR, lineWidth: BORDER_W },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: headerPad,
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        cellPadding: bodyPad,
      },
      columnStyles: workColStyles,
    })
    let y = doc.lastAutoTable.finalY

    const spareItems = data.sparePartsItems.length > 0
      ? data.sparePartsItems
      : [{ value: '', details: '', comment: '' }]

    const spareHasComments = spareItems.some((item) => item.comment.trim())
    const spareHead = spareHasComments
      ? [['Spare Parts used', 'Details on spare parts used', 'Comment']]
      : [['Spare Parts used', 'Details on spare parts used']]
    const spareBody = spareItems.map((item) =>
      spareHasComments ? [item.value, item.details, item.comment] : [item.value, item.details],
    )
    const spareColStyles: Record<number, { cellWidth: number }> = spareHasComments
      ? { 0: { cellWidth: col1W }, 1: { cellWidth: CONTENT_W - col1W - commentW }, 2: { cellWidth: commentW } }
      : { 0: { cellWidth: col1W }, 1: { cellWidth: CONTENT_W - col1W } }

    autoTable(doc, {
      head: spareHead,
      body: spareBody,
      startY: y,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: { lineColor: BORDER_COLOR, lineWidth: BORDER_W },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: headerPad,
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        cellPadding: bodyPad,
      },
      columnStyles: spareColStyles,
    })
    y = doc.lastAutoTable.finalY

    // Row 3 – Findings + Work completion (side by side checkboxes)
    y = this.drawDualCheckboxRow(
      doc,
      { label: 'Findings', options: ['Yes', 'None'], selected: data.findings || '' },
      { label: 'Work completion', options: ['Yes', 'No'], selected: data.workCompletion || '' },
      y,
    )

    // Row 5 – Recommended action(s) (only when data present)
    const hasRecommended = !!data.recommendedActions?.trim()
    if (hasRecommended) {
      autoTable(doc, {
        head: [['Recommended action(s)']],
        body: [[data.recommendedActions!]],
        startY: y,
        margin: { left: TABLE_X, right: M_LR },
        tableWidth: CONTENT_W,
        theme: 'grid',
        styles: { lineColor: BORDER_COLOR, lineWidth: BORDER_W },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: LABEL_COLOR,
          fontStyle: 'normal',
          fontSize: LABEL_SIZE,
          cellPadding: headerPad,
        },
        bodyStyles: {
          textColor: TEXT_COLOR,
          fontSize: VALUE_SIZE,
          cellPadding: bodyPad,
        },
        columnStyles: { 0: { cellWidth: CONTENT_W } },
      })
      y = doc.lastAutoTable.finalY
    }

    // Spacer area – fills remaining space; shows "Photos" header + images when present
    const signaturesH = 20
    const footerH = 8
    const images = data.inlineImages?.filter(Boolean) || []
    const hasImages = images.length > 0

    if (hasImages) {
      // "Photos" header via autoTable (header-only, no body rows)
      autoTable(doc, {
        head: [['Photos']],
        body: [],
        startY: y,
        margin: { left: TABLE_X, right: M_LR },
        tableWidth: CONTENT_W,
        theme: 'grid',
        styles: { lineColor: BORDER_COLOR, lineWidth: BORDER_W },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: LABEL_COLOR,
          fontStyle: 'normal',
          fontSize: LABEL_SIZE,
          cellPadding: headerPad,
        },
        columnStyles: { 0: { cellWidth: CONTENT_W } },
      })
      y = doc.lastAutoTable.finalY
    }

    const spacerH = Math.max(4, PAGE_H - M_BOTTOM - y - signaturesH - footerH)

    doc.setDrawColor(...BORDER_COLOR)
    doc.setLineWidth(BORDER_W)
    doc.rect(TABLE_X, y, CONTENT_W, spacerH)

    if (hasImages) {
      const pad = 3
      const gap = 3
      const innerW = CONTENT_W - pad * 2
      const innerH = spacerH - pad * 2
      const count = Math.min(images.length, 3)
      const slotW = (innerW - gap * (count - 1)) / count

      for (let i = 0; i < count; i++) {
        const slotX = TABLE_X + pad + i * (slotW + gap)
        const slotY = y + pad
        try {
          const props = doc.getImageProperties(images[i])
          const ratio = props.width / props.height
          let drawW = slotW
          let drawH = slotW / ratio
          if (drawH > innerH) {
            drawH = innerH
            drawW = innerH * ratio
          }
          const imgX = slotX + (slotW - drawW) / 2
          const imgY = slotY + (innerH - drawH) / 2
          doc.addImage(images[i], 'JPEG', imgX, imgY, drawW, drawH, '', 'FAST')
        } catch {
          doc.setFontSize(7)
          doc.setTextColor(200, 0, 0)
          doc.text('Feil', slotX + slotW / 2, slotY + innerH / 2, { align: 'center' })
        }
      }
    }

    return y + spacerH
  }

  /**
   * Draws a compact row with a label and checkbox options.
   */
  private drawCheckboxRow(
    doc: jsPDF,
    label: string,
    options: string[],
    selected: string,
    startY: number,
  ): number {
    const rowH = 7
    const boxSize = 3
    const labelW = CONTENT_W * 0.34

    doc.setDrawColor(...BORDER_COLOR)
    doc.setLineWidth(BORDER_W)
    doc.rect(TABLE_X, startY, CONTENT_W, rowH)
    doc.line(TABLE_X + labelW, startY, TABLE_X + labelW, startY + rowH)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(LABEL_SIZE)
    doc.setTextColor(...LABEL_COLOR)
    doc.text(label, TABLE_X + 2, startY + rowH / 2 + 0.5, { baseline: 'middle' })

    let xOffset = TABLE_X + labelW + 5
    const centerY = startY + rowH / 2

    for (const opt of options) {
      const boxY = centerY - boxSize / 2
      doc.setDrawColor(...BORDER_COLOR)
      doc.setLineWidth(BORDER_W)
      doc.rect(xOffset, boxY, boxSize, boxSize)

      if (selected === opt) {
        const m = 0.6
        doc.setDrawColor(50, 50, 50)
        doc.setLineWidth(0.4)
        doc.line(xOffset + m, boxY + m, xOffset + boxSize - m, boxY + boxSize - m)
        doc.line(xOffset + boxSize - m, boxY + m, xOffset + m, boxY + boxSize - m)
      }

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(VALUE_SIZE)
      doc.setTextColor(...TEXT_COLOR)
      doc.text(opt, xOffset + boxSize + 1.5, centerY + 0.3, { baseline: 'middle' })

      xOffset += boxSize + doc.getTextWidth(opt) + 8
    }

    return startY + rowH
  }

  /**
   * Draws two checkbox groups side by side in a single row.
   */
  private drawDualCheckboxRow(
    doc: jsPDF,
    left: { label: string; options: string[]; selected: string },
    right: { label: string; options: string[]; selected: string },
    startY: number,
  ): number {
    const rowH = 7
    const boxSize = 3
    const halfW = CONTENT_W / 2

    doc.setFillColor(245, 245, 245)
    doc.setDrawColor(...BORDER_COLOR)
    doc.setLineWidth(BORDER_W)
    doc.rect(TABLE_X, startY, CONTENT_W, rowH, 'FD')
    doc.line(TABLE_X + halfW, startY, TABLE_X + halfW, startY + rowH)

    const centerY = startY + rowH / 2

    for (const [side, baseX] of [[left, TABLE_X], [right, TABLE_X + halfW]] as const) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(LABEL_SIZE)
      doc.setTextColor(...LABEL_COLOR)
      doc.text(side.label, baseX + 2, centerY + 0.3, { baseline: 'middle' })

      const labelTextW = doc.getTextWidth(side.label)
      let xOffset = baseX + labelTextW + 6

      for (const opt of side.options) {
        const boxY = centerY - boxSize / 2
        doc.setDrawColor(...BORDER_COLOR)
        doc.setLineWidth(BORDER_W)
        doc.rect(xOffset, boxY, boxSize, boxSize)

        if (side.selected === opt) {
          const m = 0.6
          doc.setDrawColor(50, 50, 50)
          doc.setLineWidth(0.4)
          doc.line(xOffset + m, boxY + m, xOffset + boxSize - m, boxY + boxSize - m)
          doc.line(xOffset + boxSize - m, boxY + m, xOffset + m, boxY + boxSize - m)
        }

        doc.setFont('helvetica', 'normal')
        doc.setFontSize(VALUE_SIZE)
        doc.setTextColor(...TEXT_COLOR)
        doc.text(opt, xOffset + boxSize + 1.5, centerY + 0.3, { baseline: 'middle' })

        xOffset += boxSize + doc.getTextWidth(opt) + 6
      }
    }

    return startY + rowH
  }

  /**
   * Signature box: Serviced by (full width)
   */
  private async drawSignatures(
    data: ServiceReportData,
    startY: number,
  ): Promise<number> {
    let signatureCell: { x: number; y: number; width: number; height: number } | null = null

    autoTable(this.doc, {
      head: [['Serviced by']],
      body: [
        [''],
        [data.issuedBy || ''],
      ],
      startY,
      margin: { left: TABLE_X, right: M_LR },
      tableWidth: CONTENT_W,
      theme: 'grid',
      styles: {
        lineColor: BORDER_COLOR,
        lineWidth: BORDER_W,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: LABEL_COLOR,
        fontStyle: 'normal',
        fontSize: LABEL_SIZE,
        cellPadding: { top: 0.5, right: 2, bottom: 0, left: 2 },
        halign: 'center',
      },
      bodyStyles: {
        textColor: TEXT_COLOR,
        fontSize: VALUE_SIZE,
        halign: 'center',
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      },
      columnStyles: {
        0: { cellWidth: CONTENT_W },
      },
      didParseCell: (cellData: any) => {
        if (cellData.section === 'body' && cellData.row.index === 0) {
          cellData.cell.styles.minCellHeight = 12
        }
      },
      didDrawCell: (cellData) => {
        if (
          cellData.section === 'body' &&
          cellData.row.index === 0 &&
          cellData.column.index === 0 &&
          cellData.cell &&
          cellData.cell.width &&
          cellData.cell.height
        ) {
          signatureCell = {
            x: cellData.cell.x,
            y: cellData.cell.y,
            width: cellData.cell.width,
            height: cellData.cell.height,
          }
        }
      },
    })

    if (signatureCell && data.issuedBySignature) {
      await this.imageRenderer.addSignatureImage(this.doc, data.issuedBySignature, signatureCell)
    }

    return this.doc.lastAutoTable.finalY
  }

  /**
   * Attached images – each on its own landscape page.
   */
  private async drawImagePages(
    images: NonNullable<ServiceReportData['attachedImages']>,
    logo?: string,
  ): Promise<void> {
    const doc = this.doc

    for (let i = 0; i < images.length; i++) {
      const imgData = images[i]
      doc.addPage()

      // Header
      if (logo) {
        await this.imageRenderer.addLogo(doc, logo, {
          x: PAGE_W - M_LR - 40,
          y: M_TOP + 2,
          width: 40,
          height: 12,
        })
      }
      drawMethodTitle(doc, 'Service', TABLE_X, M_TOP + 8, {
        trailing: 'Report',
        suffix: ` - IMAGE ${i + 1}`,
      })

      // Border frame
      const frameY = M_TOP + 18
      const frameH = PAGE_H - frameY - M_BOTTOM - 10
      doc.setDrawColor(...BORDER_COLOR)
      doc.setLineWidth(BORDER_W)
      doc.rect(TABLE_X, frameY, CONTENT_W, frameH)

      let contentY = frameY + 3

      if (imgData.comment?.trim()) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(...TEXT_COLOR)
        doc.text(imgData.comment, TABLE_X + 3, contentY + 4)
        contentY += 10
      }

      try {
        const availW = CONTENT_W - 10
        const availH = frameY + frameH - contentY - 5
        const maxW = Math.min(availW * 0.7, 180)
        const maxH = Math.min(availH * 0.7, 100)
        const imgX = TABLE_X + (CONTENT_W - maxW) / 2
        const imgY = contentY + (availH - maxH) / 2
        doc.addImage(imgData.image, 'JPEG', imgX, imgY, maxW, maxH, '', 'FAST')
      } catch {
        doc.setFontSize(10)
        doc.setTextColor(200, 0, 0)
        doc.text('Feil ved lasting av bilde', PAGE_W / 2, PAGE_H / 2, { align: 'center' })
      }
    }
  }

  private addWatermark(): void {
    this.imageRenderer.addWatermark(this.doc, 'UTKAST')
  }

  private drawFooterOnAllPages(): void {
    const doc = this.doc
    const pageCount = (doc as any).getNumberOfPages?.() ?? 1
    const centerX = PAGE_W / 2
    const footerY = PAGE_H - M_BOTTOM + 6

    for (let p = 1; p <= pageCount; p++) {
      ;(doc as any).setPage?.(p)

      if (pageCount > 1) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(5.5)
        doc.setTextColor(...LABEL_COLOR)
        doc.text(`${p}/${pageCount}`, centerX, footerY - 4, { align: 'center' })
      }

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(5)
      doc.setTextColor(...TEXT_COLOR)
      doc.text(CONTROL_CARD_TEXT.footer, centerX, footerY, { align: 'center' })
    }
  }

}

export async function generateServiceReport(
  data: ServiceReportData,
  options?: ServiceTemplateOptions,
): Promise<Blob> {
  const issuedBySignature = data.issuedBySignature
    ?? (await getSignature(data.issuedBy))
    ?? undefined

  const template = new ProtoServiceTemplate()
  await template.render(
    { ...data, issuedBySignature },
    {
      finalize: options?.finalize ?? true,
      logo: options?.logo ?? STM_LOGO_SVG,
    },
  )
  return template.getBlob()
}
